import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Loading, Screen } from '../components/ui';
import { api, ApiError } from '../lib/api';
import './guest.css';

/**
 * The Bulawa — "a call has come for you" (§6).
 *
 * Two answers only: come now, or not right now. Declining tells the cart within
 * two seconds, so nobody is left staring at a screen wondering.
 */

type State =
  | { s: 'loading' }
  | { s: 'ringing'; from: string }
  | { s: 'gone'; kind: 'unknown' | 'expired' | 'declined' | 'ended' }
  | { s: 'error'; message: string };

export default function Bulawa() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [state, setState] = useState<State>({ s: 'loading' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api<{ state: string; bulawa?: { from: string } }>(`/api/g/b/${token}`);
      if (res.state === 'ringing' && res.bulawa) setState({ s: 'ringing', from: res.bulawa.from });
      else setState({ s: 'gone', kind: res.state as 'unknown' | 'expired' | 'declined' | 'ended' });
    } catch (err) {
      setState({ s: 'error', message: err instanceof ApiError ? err.message : 'Could not open this.' });
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  async function decline() {
    if (busy) return;
    setBusy(true);
    try {
      await api(`/api/g/b/${token}/decline`, { method: 'POST' });
    } finally {
      setBusy(false);
      await load();
    }
  }

  if (state.s === 'loading') {
    return (
      <Screen>
        <GuestHead word="Bulawa" />
        <Card>
          <Loading label="One moment…" />
        </Card>
      </Screen>
    );
  }

  if (state.s === 'error') {
    return (
      <Screen>
        <GuestHead word="Bulawa" />
        <Card>
          <p className="g__gone">{state.message}</p>
          <Button onClick={() => void load()}>Try again</Button>
        </Card>
      </Screen>
    );
  }

  if (state.s === 'gone') {
    const line =
      state.kind === 'declined'
        ? 'You let the shop know you cannot talk now. They will call again another time.'
        : state.kind === 'ended'
          ? 'This Mulaqaat has finished. Please phone the shop if you still need them.'
          : state.kind === 'expired'
            ? 'This Bulawa has passed. Please phone the shop and they will call you again.'
            : 'This link is not one we recognise. Please check the message again.';
    return (
      <Screen>
        <GuestHead word="Bulawa" />
        <Card>
          <p className="g__gone">{line}</p>
        </Card>
      </Screen>
    );
  }

  return (
    <Screen>
      <GuestHead word="Bulawa" />
      <Card className="g__card">
        <p className="g__ring">Bulawa aaya hai ji</p>
        <p className="g__from">{state.from} is ready to show you something.</p>

        <Button full onClick={() => navigate(`/join/${token}`)}>
          Join now
        </Button>
        <Button variant="ghost" full busy={busy} onClick={() => void decline()}>
          I can&apos;t right now
        </Button>
      </Card>
    </Screen>
  );
}

export function GuestHead({ word }: { word: string }) {
  return (
    <div className="g__head">
      <h1 className="script g__title">{word}</h1>
      <p className="g__shop">Arjun Jewellers, Kotkapura</p>
    </div>
  );
}
